import './CategoryEditBlocks.scss';
import { useState } from 'react';
import { Icon, Modal, LoadingEllipsis } from '@gplibraries/react/components';
import { useTokenProvider, useConfig } from '@gplibraries/react/hooks';
import SubcategoryEditRow from './SubcategoryEditRow.jsx';
import { useMutation } from '@tanstack/react-query';
import { iconMap } from '@gplibraries/react/config';
import { actions } from './categoriesReducer.js';
import { deleteCategory, updateCategory } from '@gplibraries/react/api';

export default function SubcategoryEditBlock(props) {

  const { category, subcategories, dispatch } = props;

  const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
  const getToken = useTokenProvider({ scopes: [dealerAccessApiScope]});
  const [showNewRow, setShowNewRow] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const { isPending: deleteCategoryLoading, mutate: removeCategory } = useMutation({
    mutationFn: (subcategory) => getToken().then(token => deleteCategory(dealerAccessApiHost, token.accessToken, {
      id: subcategory.id,
      type: subcategory.type,
    })),
    onError: (err) => {
      console.error(err)
      // display error to user
    },
    onSuccess: () => {
      dispatch({
        type: actions.REMOVE_SUBCATEGORY,
        payload: { id: toDelete.id }
      })
      setToDelete(null);
    }
  })

  const { isPending: reorderCategoryLoading, mutate: updateOrder } = useMutation({
    mutationFn: (subcategory) => getToken()
      .then(token => updateCategory(dealerAccessApiHost, token.accessToken, {
        id: subcategory.id,
        type: subcategory.type,
        weight: subcategory.weight
      }))
      .then(res => res.json()),
    onError: (err) => {
      console.error(err)
      // display error to user
    },
    onSuccess: (data) => {
      dispatch({
        type: actions.UPDATE_SUBCATEGORY,
        payload: data.data
      })
    }
  })

  const reorderSubcategory = (id, direction) => {

    if (direction !== 'up' && direction !== 'down') throw new Error('Please provide a valid direction to reorder the subcategory.');

    let reorderedSubcategories = subcategories.slice(0);
    const index = reorderedSubcategories.findIndex(cat => cat.id === id);

    if (index === 0 && direction === 'up') return;
    if (index === reorderedSubcategories.length-1 && direction === 'down') return;

    const removed = reorderedSubcategories.splice(index, 1);
    reorderedSubcategories = direction === 'up'
      ? [...reorderedSubcategories.slice(0, index-1), ...removed, ...reorderedSubcategories.slice(index-1)]
      : [...reorderedSubcategories.slice(0, index+1), ...removed, ...reorderedSubcategories.slice(index+1)];

    reorderedSubcategories.forEach((subcategory, index) => {
      if (subcategory.weight !== index) {
        updateOrder({ ...subcategory, weight: index });
      }
    })
  }

  return (
    <div className="category-wrapper flex-col">

      <Modal show={toDelete ? true : false} className={'block medium'}>
        <div className="flex-col full center right">

          <div className={'flex-col center message-box'}>

            <div className="flex-row middle headline">
              <Icon glyph={'exclamation-circle'} className={'xl danger'}/>
              <strong>WARNING:</strong>
            </div>
            {!deleteCategoryLoading &&
              <div>
                Any links under this category may need to be assigned to
                another category before displaying on dealer access again. Are
                you sure you want to delete the category <strong>{toDelete?.name ?? ''}</strong>?
              </div>
            }

            {deleteCategoryLoading && <div>Deleting category <strong>{toDelete.name}</strong><LoadingEllipsis/></div>}

          </div>

          <div className="flex-row full right middle">

            <button className="icon-button danger" disabled={deleteCategoryLoading} onClick={() => removeCategory(toDelete)}>
              <Icon glyph={'trash'}/>Delete
            </button>

            <button className="cancel" onClick={() => setToDelete(null)}>Cancel</button>

          </div>
        </div>
      </Modal>

      <div className="flex-row middle full category-heading">
        <Icon glyph={iconMap.categories[category.id].glyph}
              className={'color large'}/>
        <h3>{category.name}</h3>
      </div>

      <div className={'subcategory-wrapper no-gap flex-col full'}>
        {subcategories.map((subcat, index) => (
            <SubcategoryEditRow key={subcat.id} subcategory={subcat} dispatch={dispatch}>
              <div className="flex-row subcategory-actions">

                {(index + 1 !== 1 && subcategories.length > 1) &&
                  <button
                    className={'icon-only'}
                    title={'Move up'}
                    disabled={reorderCategoryLoading}
                    onClick={() => reorderSubcategory(subcat.id, 'up')}
                  >
                    <Icon glyph={'arrow-up-circle'}/>
                  </button>
                }

                {(index + 1 !== subcategories.length  && subcategories.length > 1) &&
                  <button
                    className={'icon-only'}
                    title={'Move down'}
                    disabled={reorderCategoryLoading}
                    onClick={() => reorderSubcategory(subcat.id, 'down')}
                  >
                    <Icon glyph={'arrow-down-circle'}/>
                  </button>
                }

                <button
                  className={'icon-only'}
                  title={'Remove category'}
                  disabled={reorderCategoryLoading}
                  onClick={() => setToDelete(subcat)}
                >
                  <Icon glyph={'trash'} className={'danger'}/>
                </button>

              </div>
            </SubcategoryEditRow>
          ))
        }
        {showNewRow &&
          <SubcategoryEditRow
            subcategory={'new'}
            setShowNewRow={setShowNewRow}
            parentCategory={category}
            dispatch={dispatch}
          >
          </SubcategoryEditRow>
        }
      </div>

      <div className="flex-row full left add-subcategory-wrapper">
        {!showNewRow &&
          <button
            className={'text-only primary'}
            onClick={() => setShowNewRow(true)}
          >
            <Icon glyph={'plus-circle'}/>
            {'Add subcategory'}
          </button>
        }
      </div>

    </div>
  );
}